import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'

import * as Actions from 'actions'
import AbstractAsset, * as Assets from 'models/Asset'
import { DRAGNDROP_TYPE, MEDIA_ERROR_TYPE, DROP_TARGET_TYPE, SEPARATOR, FILE_STATUS } from 'enums'
import { useAction } from 'hooks/utils'
import { startDragAsset as startDragAssetAction } from 'actions/timeline'
import { getIncompatibleFileExtensions } from 'helpers/incompatibleFileExtensions'
import { getDraggableType } from 'helpers/getDraggableType'
import { setActiveDragElement } from 'actions/mainView'
import { selectReferenceFilesData } from 'selectors/mainView'
import { STORAGE_CONST } from 'constant'

type Asset = InstanceType<typeof AbstractAsset> & InstanceType<typeof Assets.UploadableAsset>

type Options = {
  draggable: boolean,
  composeData: (asset: Asset) => Record<string, unknown>,
  onDenied: () => void,
  onDragStarted: () => void,
}

type DRAGNDROP_TYPE = typeof DRAGNDROP_TYPE[keyof typeof DRAGNDROP_TYPE]

type DragObject = {
  type: DRAGNDROP_TYPE,
  id: string,
  asset: Asset,
  duration: number,
  incompatibleFileExtensions: Record<string, ReturnType<typeof getIncompatibleFileExtensions>>,
}
type CollectedProps = Record<string, unknown>
type DropResult = Record<string, unknown>

const noop = () => {}

function useDraggable(asset: Asset, options: Options) {
  const selectedFiles = useSelector(selectReferenceFilesData)
  const setDndDropTarget = useAction(Actions.mainView.setDndDropTarget)
  const onSetActiveDragElement = useAction(setActiveDragElement)
  const startDragAsset = useAction(startDragAssetAction, asset.id)
  const { composeData = noop, onDenied = noop, onDragStarted = noop, draggable = true } = options
  const onSetDndDropTarget = useAction(setDndDropTarget)
  const fileExtension = asset.filetype || asset.name?.split(SEPARATOR.DOT).at(-1) || ''
  const item = useMemo(() => ({
    ...composeData(asset),
    type: getDraggableType(asset),
    id: asset.id,
    asset,
    duration: DRAGNDROP_TYPE.TRANSITION_ITEM === getDraggableType(asset)
      ? JSON.parse(
        sessionStorage.getItem(STORAGE_CONST.DEFAULT_TRANSITION_DURATION)
          || JSON.stringify({ [asset.type]: asset.duration })
      )?.[asset.type]
      : asset.duration,
    incompatibleFileExtensions: {
      [fileExtension]: getIncompatibleFileExtensions(asset.filetype),
    },
  }), [ asset, composeData, fileExtension ])

  const [ state, drag, preview ] = useDrag<DragObject, DropResult, CollectedProps>({
    item,
    begin() {
      startDragAsset()
      onSetActiveDragElement(item.type)
      onDragStarted()
      setDndDropTarget(DROP_TARGET_TYPE.ROOT)
    },
    end() {
      onSetActiveDragElement(null)
      onSetDndDropTarget(DROP_TARGET_TYPE.ROOT)
    },
    canDrag() {
      const readyDragStatus = [ FILE_STATUS.READY, FILE_STATUS.PREPARE_THUMBNAILS ]
      if (!draggable
          || selectedFiles.length > 1
          || selectedFiles.find(item => !readyDragStatus.includes(item.status))) {
        return false
      }
      // do not allow drag items failed to upload and show
      // message box if uploading item is dragging
      if (asset.uploading
        || (asset.error && asset.error.type !== MEDIA_ERROR_TYPE.BUILD_THUMBNAILS)) {
        onDenied()
        return false
      }
      return true
    },
  })

  // Suppress default generated preview, because we need custom drag preview behavior.
  // See components/Timeline/DragAndDrop/CustomDragLayer.jsx
  React.useEffect(
    () => { preview(getEmptyImage()) },
    [ preview ]
  )

  return [ state, drag, startDragAsset ]
}

export default useDraggable
